/*
 * Copyright (c) 2019. Sonrai Analytics Ltd.
 * Developed by Ryan Duffy
 */

import { Injectable, OnDestroy, Injector } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { HistoryActionService } from '../api-services/history-actions/history-actions.service';
import { HttpRequest } from '@angular/common/http';
import * as uuid from '../../../../node_modules/uuid';
import { take } from 'rxjs/operators';
import { HistoryService } from '../api-services/history/history.service';
import { SubSink } from '../../../../node_modules/subsink/dist/subsink';
import { AuthenticationService } from '../authentication/authentication.service';
import { ApiService } from '../api-services/api.service';
import { ProjectsListService } from '../api-services/projects/projects-list.service';

@Injectable({ providedIn: 'root' })
export class HistoryHttpService implements OnDestroy {
  // Behaviour subjects required to ensure eventIDs do not renew everytime the service is called
  private eventIDSubject: BehaviorSubject<string>;
  private targetIDSubject: BehaviorSubject<string>;
  // Timestamps to ensure Events and actions actually relate to eachother
  private eventTimestamp: Date;
  private actionTimestamp: Date;
  // Target Name added to ensure timeline can retain the names of deleted Projects, Users etc.
  private targetNameSubject: BehaviorSubject<string>;
  // Variables that will be set everytime this service is instantiated
  private endpoint: string;
  private newData: any;
  private oldData: any;
  // Used to detemine whether the action will also insert into events i.e. projects-list, orgs etc.
  // If false, will only insert into the history_actions table
  private insertEvent: boolean;
  // Subsink to be used in the OnDestroy Lifecycle hook to destroy all active subscriptions
  private subscriptionContainer = new SubSink();

  constructor(
    private injector: Injector,
    private apiService: ApiService,
    private projectListService: ProjectsListService
  ) {}
  handleApiRequest(request: HttpRequest<any>) {
    // Due to depency overlaps and issues with constructor insert the services needed using an injector
    const historyService = this.injector.get(HistoryService);
    const historyActionService = this.injector.get(HistoryActionService);
    const authService = this.injector.get(AuthenticationService);
    let timeDifference: number;
    // For each request the endpoint can be fetched using the headers
    // Ensure no GET requests are handled, at all!
    if (request.method !== 'GET') {
      if (
        // Ensure that the 'history and history_actions tables are not handled
        // Throws a horrible INFINITE LOOP without this check in place
        request.headers.get('endpoint') !== 'history' &&
        request.headers.get('endpoint') !== 'history_actions' &&
        request.headers.get('endpoint') !== 'notifications' &&
        request.headers.get('endpoint') !== 'notification_users' &&
        request.headers.get('endpoint') !== 'data_templates' &&
        request.headers.get('endpoint') !== 'organisation_data_templates'
      ) {
        this.endpoint = request.headers.get('endpoint');
        // Switch to determine the variables that are set
        // Firstly set the most repeatable variables not dependent on endpoint
        switch (request.method) {
          // ALL requests will be passed through this switch so this is where their details are set
          // this will include project_users etc, and as such will take the eventID current set
          // WHich is done previously by the project-analytics POST/PATCH/DELETE
          case 'POST':
            this.oldData = {};
            this.newData = request.body;
            break;
          case 'PATCH':
            this.oldData = request.headers.get('data');
            this.newData = request.body;
            break;
          case 'DELETE':
            this.oldData = request.headers.get('data');
            this.newData = {};
            break;
        }
        switch (this.endpoint) {
          // secondly set the varialbles that rely on the endpoint to be set correctly
          // Projects should set the eventID, targetID and be pushed to the events table
          case 'projects':
            this.projectsHistory(request);
            break;
          case 'project_tasks':
            this.projectTasksHistory(request);
            break;
          case 'project_notes':
            this.projectNotesHistory(request);
            break;
          case 'project_users':
            this.newData = request.body;
            this.projectsUsers(request);
            break;
          case 'app_data':
            this.appDataHistory(request);
            break;
          case 'datasets_projects':
            this.datasetsProjectsHistory(request);
            break;
          case 'roles':
            this.rolesHistory(request);
            break;
          // All other tables do not get inserted into the history Table, only agreed events
          // At the minute only Projects - probable modification in future
          default:
            this.insertEvent = false;
            // Get the time of this action being executed
            this.actionTimestamp = new Date();
            // If this action happened over 3 seconds after the event, they should be not be linked
            // TODO: Make this something more fail safe - bit tricky, however.
            if (this.eventTimestamp) {
              timeDifference =
                (this.actionTimestamp.getTime() - this.eventTimestamp.getTime()) / 1000;
              if (timeDifference > 5) {
                this.eventIDSubject = null;
              }
            } else {
              this.eventIDSubject = null;
            }
        }
        // Ensure that an eventID and targetID is set - otherwise it doesn't need to be inserted
        if (this.eventIDSubject) {
          // Subscribe to the current target and eventID so we can insert the correct data for each event and target
          // Usage of take(1) - looked into and can see no issue, as it completes the subscriptions after the first value
          // take(1) - more of a safeguard in this instance
          this.subscriptionContainer.sink = this.eventIDSubject
            .pipe(take(1))
            .subscribe((eventID) => {
              this.subscriptionContainer.sink = this.targetIDSubject
                .pipe(take(1))
                .subscribe((targetID) => {
                  // Insert the relevant history record for the project-analytics and others in future (used for the timeline)
                  if (this.insertEvent === true) {
                    // TargetName only relates to insert event - so only bother with it in this case
                    this.subscriptionContainer.sink = this.targetNameSubject
                      .pipe(take(1))
                      .subscribe((targetName) => {
                        historyService.insertData(
                          {},
                          {
                            event_id: eventID,
                            event_type: request.method,
                            event_target_type: this.endpoint,
                            changed_by: authService.user.userId,
                            timestamp: new Date(),
                            target_id: targetID,
                            target_name: targetName,
                          }
                        );
                      });
                  }
                  this.subscriptionContainer.sink = historyService.insertSubject
                    .pipe(take(1))
                    // Put here in case actions wish to be carried out in future in this part of the code
                    // (Also to allow for unsub at the bottom)
                    .subscribe();
                  // Insert the relevant history_actions record for the project-analytics (used for rollbacks)
                  historyActionService.insertData(
                    {},
                    {
                      action_id: uuid.v4(),
                      event_id: eventID,
                      target_id: targetID,
                      action_type: request.method,
                      action_target_type: this.endpoint,
                      timestamp: new Date(),
                      new_value: this.newData,
                      old_value: this.oldData,
                    }
                  );
                });
            });
        }
      }
    }
  }

  /*
  Creates audit records when Projects are created, updated or deleted.
  Is called by HandleAPIRequest()
  */
  projectsHistory(request: HttpRequest<any>) {
    // Projects should set the eventID, targetID and be pushed to the events table
    this.insertEvent = true;
    this.eventTimestamp = new Date();
    // EventID set so the other actions associated will be tied to this project-analytics action
    this.eventIDSubject = new BehaviorSubject<string>(uuid.v4());
    switch (request.method) {
      // For each type of request, get different data to provide to the history/event record
      case 'POST':
        this.targetIDSubject = new BehaviorSubject<string>(request.body.project_id);
        this.targetNameSubject = new BehaviorSubject<string>(request.body.project_name);
        break;
      case 'PATCH':
        this.targetIDSubject = new BehaviorSubject<string>(this.oldData.project_id);
        this.targetNameSubject = new BehaviorSubject<string>(this.oldData.project_name);
        break;
      case 'DELETE':
        this.targetIDSubject = new BehaviorSubject<string>(this.oldData.project_id);
        this.targetNameSubject = new BehaviorSubject<string>(this.oldData.project_name);
        break;
    }
  }

  /*
  Creates audit records when project tasks are created, updated or deleted.
  Is called by HandleAPIRequest()
  */
  projectTasksHistory(request: HttpRequest<any>) {
    this.eventIDSubject = new BehaviorSubject<string>(uuid.v4());
    this.eventTimestamp = new Date();
    this.insertEvent = true;
    switch (request.method) {
      case 'POST':
        this.targetIDSubject = new BehaviorSubject<string>(request.body.project_id);
        this.targetNameSubject = new BehaviorSubject<string>(
          this.projectListService.getProjectsById(request.body.project_id)?.projectName
        );
        break;
      case 'PATCH':
        this.targetIDSubject = new BehaviorSubject<string>(this.oldData.project_id);
        this.targetNameSubject = new BehaviorSubject<string>(
          this.projectListService.getProjectsById(this.oldData.project_id).projectName
        );
        break;
      case 'DELETE':
        this.targetIDSubject = new BehaviorSubject<string>(this.oldData.project_id);
        this.targetNameSubject = new BehaviorSubject<string>(
          this.projectListService.getProjectsById(this.oldData.project_id).projectName
        );
        break;
    }
  }

  /*
  Creates audit records when project notes are created, updated or deleted.
  Is called by HandleAPIRequest()
  */
  projectNotesHistory(request: HttpRequest<any>) {
    this.eventIDSubject = new BehaviorSubject<string>(uuid.v4());
    this.eventTimestamp = new Date();
    this.insertEvent = true;
    switch (request.method) {
      case 'POST':
        this.targetIDSubject = new BehaviorSubject<string>(request.body.project_id);
        this.targetNameSubject = new BehaviorSubject<string>(
          this.projectListService.getProjectsById(request.body.project_id).projectName
        );
        break;
      case 'PATCH':
        this.targetIDSubject = new BehaviorSubject<string>(this.oldData.project_id);
        this.targetNameSubject = new BehaviorSubject<string>(
          this.projectListService.getProjectsById(this.oldData.project_id).projectName
        );
        break;
      case 'DELETE':
        this.targetIDSubject = new BehaviorSubject<string>(this.oldData?.project_id);
        this.targetNameSubject = new BehaviorSubject<string>(
          this.projectListService.getProjectsById(this.oldData?.project_id)?.projectName
        );
        break;
    }
  }

  projectsUsers(request: HttpRequest<any>) {
    // Projects should set the eventID, targetID and be pushed to the events table
    this.insertEvent = true;
    this.eventTimestamp = new Date();
    // EventID set so the other actions associated will be tied to this project-analytics action
    this.eventIDSubject = new BehaviorSubject<string>(uuid.v4());
    const body = request.body;
    switch (request.method) {
      // For each type of request, get different data to provide to the history/event record
      case 'POST':
        this.targetIDSubject = new BehaviorSubject<string>(body.project_id);
        this.targetNameSubject = new BehaviorSubject<string>(
          this.projectListService.getProjectsById(Array.isArray(body) ? body[0].project_id : body.project_id).projectName
        );
        break;
      case 'PATCH':
        this.targetIDSubject = new BehaviorSubject<string>(this.oldData?.project_id);
        this.targetNameSubject = new BehaviorSubject<string>(
          this.projectListService.getProjectsById(this.oldData?.project_id)?.projectName
        );
        break;
      case 'DELETE':
        this.targetIDSubject = new BehaviorSubject<string>(this.oldData.project_id);
        this.targetNameSubject = new BehaviorSubject<string>(
          this.projectListService.getProjectsById(this.oldData.project_id)?.projectName
        );
        break;
    }
  }

  /*
  Creates audit records when project data is created, updated or deleted.
  Is called by HandleAPIRequest()
  */
  appDataHistory(request: HttpRequest<any>) {
    this.eventIDSubject = new BehaviorSubject<string>(uuid.v4());
    this.eventTimestamp = new Date();
    this.insertEvent = true;
    // Don't add if no project ID available
    // OR no descriptive changes (request body length will be 1)
    if (
      !request.body ||
      !request.body.length ||
      !request.body[0] ||
      !this.oldData ||
      (!request.body[0]?.project_id && !this.oldData.project_id) ||
      Object.keys(request.body[0]).length === 1
    ) {
      this.eventIDSubject = null;
      return;
    }
    switch (request.method) {
      case 'POST':
        this.targetIDSubject = new BehaviorSubject<string>(request.body[0].project_id);
        this.targetNameSubject = new BehaviorSubject<string>(
          this.projectListService.getProjectsById(request.body[0].project_id).projectName
        );
        break;
      case 'PATCH':
        this.targetIDSubject = new BehaviorSubject<string>(this.oldData.project_id);
        this.targetNameSubject = new BehaviorSubject<string>(
          this.oldData.project_id
            ? this.projectListService.getProjectsById(this.oldData.project_id).projectName
            : ''
        );
        break;
      case 'DELETE':
        this.targetIDSubject = new BehaviorSubject<string>(this.oldData.project_id);
        this.targetNameSubject = new BehaviorSubject<string>(
          this.projectListService.getProjectsById(this.oldData.project_id).projectName
        );
        break;
    }
  }

  /*
  Creates audit records when datasets are linked to projects
  Is called by HandleAPIRequest()
  */
  datasetsProjectsHistory(request: HttpRequest<any>) {
    this.eventIDSubject = new BehaviorSubject<string>(uuid.v4());
    this.eventTimestamp = new Date();
    this.insertEvent = true;
    // Don't add if no project ID available
    // OR no descriptive changes (request body length will be 1)
    if (
      !request.body ||
      !request.body.length ||
      (!request.body[0].project_id && !this.oldData.project_id) ||
      Object.keys(request.body[0]).length === 1
    ) {
      this.eventIDSubject = null;
      return;
    }
    switch (request.method) {
      case 'POST':
        this.targetIDSubject = new BehaviorSubject<string>(request.body[0].project_id);
        this.targetNameSubject = new BehaviorSubject<string>(
          this.projectListService.getProjectsById(request.body[0].project_id).projectName
        );
        break;
      case 'PATCH':
        this.targetIDSubject = new BehaviorSubject<string>(this.oldData.project_id);
        this.targetNameSubject = new BehaviorSubject<string>(
          this.projectListService.getProjectsById(this.oldData.project_id).projectName
        );
        break;
      case 'DELETE':
        this.targetIDSubject = new BehaviorSubject<string>(this.oldData.project_id);
        this.targetNameSubject = new BehaviorSubject<string>(
          this.projectListService.getProjectsById(this.oldData.project_id).projectName
        );
    }
  }

  // Tasks should set the eventID, targetID and NOT be pushed to the events table
  // This will mean task_users created etc. will be associated with the overall eventID
  rolesHistory(request: HttpRequest<any>) {
    this.insertEvent = true;
    this.eventTimestamp = new Date();
    this.eventIDSubject = new BehaviorSubject<string>(uuid.v4());
    switch (request.method) {
      case 'POST':
        this.targetIDSubject = new BehaviorSubject<string>(request.body.role_id);
        this.targetNameSubject = new BehaviorSubject<string>(request.body.role_name);
        break;
      case 'PATCH':
        this.targetIDSubject = new BehaviorSubject<string>(this.oldData.role_id);
        this.targetNameSubject = new BehaviorSubject<string>(this.oldData.role_name);
        break;
      case 'DELETE':
        this.targetIDSubject = new BehaviorSubject<string>(this.oldData.role_id);
        this.targetNameSubject = new BehaviorSubject<string>(this.oldData.role_name);
        break;
    }
  }

  ngOnDestroy(): void {
    this.subscriptionContainer.unsubscribe();
  }
}
